// ===========================================
// Xero API Functions
// ===========================================
// API calls for Xero integration endpoints
// OAuth, sync control, account mappings

import { apiClient } from '../services/api';

// ===========================================
// TYPES
// ===========================================

export interface XeroConnectionStatus {
  isConnected: boolean;
  tenantId: string | null;
  tenantName: string | null;
  syncEnabled: boolean;
  syncInvoices: boolean;
  syncPayments: boolean;
  autoCreateContacts: boolean;
  lastSyncAt: string | null;
  hasErrors: boolean;
  accountMappings: {
    salesAccountCode: string | null;
    receivablesAccount: string | null;
    bankAccountId: string | null;
  };
}

export interface XeroAccount {
  accountId: string;
  code: string;
  name: string;
  type: string;
  status: string;
}

export interface XeroBankAccount {
  accountId: string;
  name: string;
  code: string;
  currencyCode: string;
}

export interface XeroSyncStats {
  contacts: {
    total: number;
    synced: number;
    errors: number;
  };
  invoices: {
    total: number;
    synced: number;
    pending: number;
    errors: number;
  };
  recentLogs: Array<{
    id: string;
    syncType: string;
    entityType: string;
    status: string;
    startedAt: string;
    completedAt: string | null;
    errorMessage: string | null;
  }>;
}

export interface XeroSyncError {
  invoices: Array<{
    invoiceId: string;
    invoiceNumber: string;
    error: string;
  }>;
  contacts: Array<{
    familyId: string;
    familyName: string;
    error: string;
  }>;
}

export interface XeroSyncResult {
  contacts?: {
    synced: number;
    errors: number;
  };
  invoices?: {
    synced: number;
    errors: number;
  };
}

export interface UpdateSettingsInput {
  syncEnabled?: boolean;
  syncInvoices?: boolean;
  syncPayments?: boolean;
  autoCreateContacts?: boolean;
}

export interface AccountMappingInput {
  salesAccountCode?: string | null;
  receivablesAccount?: string | null;
  bankAccountId?: string | null;
}

export interface TriggerSyncInput {
  syncType?: 'full' | 'invoices' | 'contacts';
  invoiceIds?: string[];
}

// ===========================================
// API RESPONSE WRAPPERS
// ===========================================

interface ApiResponse<T> {
  status: string;
  data: T;
  message?: string;
}

// ===========================================
// XERO API
// ===========================================

export const xeroApi = {
  // ===========================================
  // OAUTH AUTHENTICATION
  // ===========================================

  /**
   * Get OAuth authorization URL to connect Xero
   * Admin only
   */
  getAuthUrl: (): Promise<{ authUrl: string }> =>
    apiClient
      .get<ApiResponse<{ authUrl: string }>>('/integrations/xero/auth-url')
      .then((res) => res.data),

  /**
   * Get Xero connection status
   * Admin only
   */
  getStatus: (): Promise<XeroConnectionStatus> =>
    apiClient
      .get<ApiResponse<XeroConnectionStatus>>('/integrations/xero/status')
      .then((res) => res.data),

  /**
   * Disconnect Xero integration
   * Admin only
   */
  disconnect: (): Promise<void> =>
    apiClient
      .delete('/integrations/xero')
      .then(() => undefined),

  // ===========================================
  // SETTINGS MANAGEMENT
  // ===========================================

  /**
   * Update Xero integration settings
   * Admin only
   */
  updateSettings: (input: UpdateSettingsInput): Promise<{
    syncEnabled: boolean;
    syncInvoices: boolean;
    syncPayments: boolean;
    autoCreateContacts: boolean;
  }> =>
    apiClient
      .put<ApiResponse<{
        syncEnabled: boolean;
        syncInvoices: boolean;
        syncPayments: boolean;
        autoCreateContacts: boolean;
      }>>('/integrations/xero/settings', input)
      .then((res) => res.data),

  /**
   * Update account mappings
   * Admin only
   */
  updateAccountMappings: (input: AccountMappingInput): Promise<{
    salesAccountCode: string | null;
    receivablesAccount: string | null;
    bankAccountId: string | null;
  }> =>
    apiClient
      .put<ApiResponse<{
        salesAccountCode: string | null;
        receivablesAccount: string | null;
        bankAccountId: string | null;
      }>>('/integrations/xero/account-mappings', input)
      .then((res) => res.data),

  // ===========================================
  // ACCOUNT LISTING
  // ===========================================

  /**
   * List available Xero accounts
   * Admin only
   */
  listAccounts: (): Promise<XeroAccount[]> =>
    apiClient
      .get<ApiResponse<{ accounts: XeroAccount[] }>>('/integrations/xero/accounts')
      .then((res) => res.data.accounts),

  /**
   * List available Xero bank accounts
   * Admin only
   */
  listBankAccounts: (): Promise<XeroBankAccount[]> =>
    apiClient
      .get<ApiResponse<{ accounts: XeroBankAccount[] }>>('/integrations/xero/bank-accounts')
      .then((res) => res.data.accounts),

  // ===========================================
  // SYNC MANAGEMENT
  // ===========================================

  /**
   * Trigger manual sync
   * Admin only
   */
  triggerSync: (input?: TriggerSyncInput): Promise<XeroSyncResult> =>
    apiClient
      .post<ApiResponse<XeroSyncResult>>('/integrations/xero/sync', input || {})
      .then((res) => res.data),

  /**
   * Get sync statistics
   * Admin only
   */
  getSyncStats: (): Promise<XeroSyncStats> =>
    apiClient
      .get<ApiResponse<XeroSyncStats>>('/integrations/xero/sync/stats')
      .then((res) => res.data),

  /**
   * Get entities with sync errors
   * Admin only
   */
  getSyncErrors: (): Promise<XeroSyncError> =>
    apiClient
      .get<ApiResponse<XeroSyncError>>('/integrations/xero/sync/errors')
      .then((res) => res.data),

  /**
   * Retry sync for a specific entity
   * Admin only
   */
  retrySync: (entityType: 'invoice' | 'payment' | 'contact', entityId: string): Promise<void> =>
    apiClient
      .post('/integrations/xero/sync/retry', { entityType, entityId })
      .then(() => undefined),
};

// ===========================================
// HELPER FUNCTIONS
// ===========================================

/**
 * Format timestamp for display
 */
export const formatSyncTime = (timestamp: string | null): string => {
  if (!timestamp) return 'Never';

  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
};

/**
 * Get sync status color for display
 */
export const getSyncStatusColor = (
  status: string
): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
  switch (status) {
    case 'SYNCED':
      return 'success';
    case 'SYNCING':
      return 'primary';
    case 'ERROR':
      return 'error';
    case 'PENDING':
      return 'warning';
    default:
      return 'default';
  }
};
